// /app/frontend/src/pages/seo/SisterWivesPolyFamilyBuildingPage.jsx
// Focus: STRUCTURE & DYNAMICS once a plural family is forming.
import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import {
  SEOHead,
  organizationSchema,
  buildBreadcrumbSchema,
  GradientHero,
  FeatureCard,
  RelatedGuides,
  BottomCTA,
} from './SEOUtils';

export default function SisterWivesPolyFamilyBuildingPage() {
  const path = '/sister-wives-and-poly-family-building';
  const schemas = [
    organizationSchema,
    buildBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Guides', url: '/guides' },
      { name: 'Sister Wives & Poly Family Building', url: path },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="Sister Wives & Poly Family Building"
        description="Explore healthy family-building, communication, shared goals, and intentional relationship structures."
        canonical={path}
        schemas={schemas}
      />

      <GradientHero
        eyebrow="A UnityLove Guide"
        headline="Building Healthy Poly Families"
        sub="Once the family begins to form, the work shifts. From dating to dynamics — here is how the strongest sister-wife and plural families actually run."
        body="This guide is for established and forming plural families navigating the realities of shared life: time, space, parenting, decisions, and identity."
        primaryCta={{ label: 'Join UnityLove', to: '/auth?mode=signup' }}
        secondaryCta={{ label: 'How To Find A Sister Wife', to: '/how-to-find-a-sister-wife' }}
      />

      <main className="max-w-4xl mx-auto px-4 py-14">
        {/* Intro */}
        <section className="mb-12 max-w-2xl mx-auto text-center">
          <p className="text-lg text-slate-700 leading-relaxed">
            Dating is one chapter. The family that follows is the rest of the book. Healthy plural families share a small number of patterns — and the patterns are mostly invisible until you go looking for them.
          </p>
        </section>

        {/* The four operating dimensions */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-6">
            Four Dimensions That Keep Plural Families Healthy
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <FeatureCard
              icon="⏰"
              accent="rose"
              title="Time"
              body="How is shared time managed? Most healthy families have explicit time rhythms — equal nights, named anchor days, scheduled couple-only and family-wide windows. Implicit time always becomes uneven time."
            />
            <FeatureCard
              icon="🗣️"
              accent="emerald"
              title="Communication"
              body="One-on-one check-ins between every pair, plus regular full-family meetings. The families that thrive treat communication as infrastructure, not as something that happens when there is a problem."
            />
            <FeatureCard
              icon="🏠"
              accent="sky"
              title="Space"
              body="Whether one household or many, each wife needs autonomy of space — somewhere that is hers to control. The strongest families resist over-merging in the first year."
            />
            <FeatureCard
              icon="💼"
              accent="amber"
              title="Resources"
              body="Money, labor, decision-making. Transparency about who has access to what, who contributes what, and who gets a vote on what — written down, revisited yearly."
            />
          </div>
        </section>

        {/* Sister-wife relationships */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-3">
            The Sister-Wife Bond Itself
          </h2>
          <div className="space-y-3 text-slate-700 leading-relaxed">
            <p>
              In many polygamous families, the most underestimated relationship is not between husband and wives — it is between the wives themselves. Two women sharing a husband can become rivals, allies, friends, sisters, or polite strangers. The shape of that bond determines almost everything else.
            </p>
            <p>
              Healthy plural families intentionally invest in the sister-wife relationship. Time together without the husband. Shared decisions where she is asked first. Public language that names her as family, not as a category. Many wives describe this bond as the deepest friendship of their adult life — but only when it is built on purpose.
            </p>
          </div>
        </section>

        {/* Decision-making */}
        <section className="mb-14 bg-slate-50 rounded-3xl p-6 sm:p-8">
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
            How Decisions Get Made
          </h2>
          <p className="text-slate-700 leading-relaxed mb-4">
            One of the first things plural families need to figure out is how decisions actually happen. Most fall into one of three patterns — and the troubled ones usually have not picked yet:
          </p>
          <ul className="space-y-3 text-slate-700">
            <li>
              <strong className="text-slate-900">Consensus model.</strong> Major decisions require agreement from every adult. Slow but binding. Works best in mature families with strong communication.
            </li>
            <li>
              <strong className="text-slate-900">Domain model.</strong> Each adult owns certain domains (finances, parenting style, household choices) and is the final decision-maker there. Fast and respects autonomy.
            </li>
            <li>
              <strong className="text-slate-900">Hierarchical model.</strong> One person (typically the husband, sometimes a first wife) has tie-breaking authority. Common in faith-rooted families. Works when paired with deep listening; fails when it skips the listening.
            </li>
          </ul>
          <p className="text-slate-700 mt-4 text-sm italic">
            None of these is morally better than the others. The danger is not the model — it is leaving the model implicit.
          </p>
        </section>

        {/* Parenting together */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-3">
            Parenting Across A Plural Family
          </h2>
          <div className="space-y-3 text-slate-700 leading-relaxed">
            <p>
              Children inside plural families often describe one of the deepest gifts of the structure: more loving adults. More attention. More people who know them. But this only emerges when the adults coordinate.
            </p>
            <p>
              Strong plural families align on the basics — discipline philosophy, screen time, language about the family structure used outside the home, role of each adult in each child’s life. Disagreements are normal; what matters is that children do not become triangulation points.
            </p>
          </div>
        </section>

        {/* What can break a family */}
        <section className="mb-14 rounded-2xl border border-amber-200 bg-amber-50 p-6 sm:p-8">
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-amber-900 mb-4">
            What Breaks Plural Families
          </h2>
          <ul className="space-y-2 text-amber-900 list-disc list-inside">
            <li>Unequal time that goes unspoken for too long</li>
            <li>A wife who feels disposable instead of chosen</li>
            <li>Financial opacity</li>
            <li>The husband (or a wife) becoming a courier of information between the others instead of having direct conversations</li>
            <li>Big life changes — pregnancy, illness, job loss — without proactive renegotiation</li>
            <li>Outside pressure (family, faith community, work) that fractures alignment</li>
          </ul>
          <p className="mt-4 text-amber-900 text-sm">
            None of these are death sentences. Every one of them is addressable when surfaced early.
          </p>
        </section>

        {/* Internal links */}
        <section className="mb-4">
          <h2 className="font-playfair text-2xl font-bold text-slate-900 mb-4">
            Read Next
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <a href="/building-a-polygamous-family" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Building A Polygamous Family</p>
              <p className="text-sm text-slate-600 mt-1">Values, trust, and a strong foundation.</p>
            </a>
            <a href="/poly-family-planning" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Poly Family Planning</p>
              <p className="text-sm text-slate-600 mt-1">Practical planning across plural structures.</p>
            </a>
            <a href="/how-to-find-a-sister-wife" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">How To Find A Sister Wife</p>
              <p className="text-sm text-slate-600 mt-1">A respectful step-by-step guide.</p>
            </a>
            <a href="/safe-poly-dating" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Safe Poly Dating Guide</p>
              <p className="text-sm text-slate-600 mt-1">Practical safety for healthy poly relationships.</p>
            </a>
          </div>
        </section>
      </main>

      <RelatedGuides currentPath={path} />
      <BottomCTA />
    </div>
  );
}
